import { Box, CircularProgress, DialogContent, DialogTitle, Grid, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import CustomDialog from "components/custom/CustomDialog";
import HeaderOuBannerDependendoDaInterpretacaoH1 from "layout/header-ou-banner-dependendo-da-interpretacao-h1/HeaderOuBannerDependendoDaInterpretacaoH1";
import { useCallback, useEffect, useRef, useState } from "react";

import FinancasService from "./financas.service";
import useCatchApiError from "hooks/useCatchApiError";
import { captalizeString, ColumnData, currencyOf, isEmpty } from "utils/util";
import VisualizarItem from "./VisualizarItem";
import { TabContext, TabList } from "@mui/lab";
import CustomTableCollapsable from "components/CustomTableCollapsable";
import {
    VisualizarContaReceberRender,
    FlowContaReceberVisualizacaoDTO,
    TVisualizarContasReceberProps,
    FlowContaReceberDadosRecebimentoDTO,
    FlowContaReceberDadosChequeDTO,
    FlowContaReceberBaixa,
    FlowContaReceberDadosRecebimentoItensDTO,
    TDadosBaixaHeader,
    FlowContaReceberCartaoDTO
} from "./financas.model";
import { theme } from "theme";

const initialState = {
    tipoRender: VisualizarContaReceberRender.IDENTIFICACAO as VisualizarContaReceberRender,
    flowContaReceberVisualizarDTO: null as FlowContaReceberVisualizacaoDTO | null,
    isLoading: true,
    colapsadas: [] as Array<string>
}

const tabHeaderStyle = {
    '&.Mui-selected': {
        color: theme.palette.primary.main
    },
    fontSize: 12,
    letterSpacing: 2,
    flexShrink: 0,
};

const VisualizarContaReceber = (props: TVisualizarContasReceberProps) => {
    const [stateLocal, setStateLocal] = useState(initialState)
    const financasService = FinancasService()
    const { throwErrorMessage } = useCatchApiError()
    const isFirstRenderRef = useRef<boolean>(true)

    const onSetTipoTela = useCallback((tipoRender: VisualizarContaReceberRender) => {
        return setStateLocal(prevState => ({ ...prevState, tipoRender }))
    }, [])


    const getDadosVisualizar = useCallback(async () => {
        try {
            const { data } = await financasService.getVisualizarContasReceber(props.uuid)
            setStateLocal(prevState => ({ ...prevState, flowContaReceberVisualizarDTO: data, isLoading: false }))
        } catch (err) {
            setStateLocal(prevState => ({ ...prevState, isLoading: false }))
            props.onClose()
            throwErrorMessage(err ?? [])
        }
        //eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (isFirstRenderRef.current) {
            getDadosVisualizar()
        }
        isFirstRenderRef.current = false
        //eslint-disable-next-line
    }, [])

    const isIdentificacaoRender = useCallback(() => {
        return stateLocal.tipoRender === VisualizarContaReceberRender.IDENTIFICACAO;
        //eslint-disable-next-line
    }, [stateLocal.tipoRender]);

    const isDadosCobrancaRender = useCallback(() => {
        return stateLocal.tipoRender === VisualizarContaReceberRender.DADOS_COBRANCA;
        //eslint-disable-next-line
    }, [stateLocal.tipoRender]);

    const isDadosCheque = useCallback(() => {
        return stateLocal.tipoRender === VisualizarContaReceberRender.DADOS_CHEQUE;
        //eslint-disable-next-line
    }, [stateLocal.tipoRender]);

    const isDadosBaixa = useCallback(() => {
        return stateLocal.tipoRender === VisualizarContaReceberRender.DADOS_BAIXA;
        //eslint-disable-next-line
    }, [stateLocal.tipoRender]);

    const isDadosRecebimento = useCallback(() => {
        return stateLocal.tipoRender === VisualizarContaReceberRender.DADOS_RECEBIMENTO;
        //eslint-disable-next-line
    }, [stateLocal.tipoRender]);

    const isHistoricoMovimentacaoRender = useCallback(() => {
        return stateLocal.tipoRender === VisualizarContaReceberRender.HISTORICO_MOVIMENTACAO;
        //eslint-disable-next-line
    }, [stateLocal.tipoRender]);

    const isRepresentantesRender = useCallback(() => {
        return stateLocal.tipoRender === VisualizarContaReceberRender.REPRESENTANTES;
        //eslint-disable-next-line
    }, [stateLocal.tipoRender]);

    const isHistoricoAlteracaoVencimentoRender = useCallback(() => {
        return stateLocal.tipoRender === VisualizarContaReceberRender.HISTORICO_ALTERACAO_VENCIMENTO;
        //eslint-disable-next-line
    }, [stateLocal.tipoRender]);

    const isContasApropriacoesRender = useCallback(() => {
        return stateLocal.tipoRender === VisualizarContaReceberRender.CONTAS_APROPRIACOES;
        //eslint-disable-next-line
    }, [stateLocal.tipoRender]);

    const isObservacaoRender = useCallback(() => {
        return stateLocal.tipoRender === VisualizarContaReceberRender.OBSERVACAO;
        //eslint-disable-next-line
    }, [stateLocal.tipoRender]);

    const getContasReceberDadosRecebimentoMessage = useCallback((dadosRecebimento: FlowContaReceberDadosRecebimentoDTO | null | undefined) => {
        if (!isEmpty(dadosRecebimento?.recebimentos)) return "Recebimentos"
        let hasCheque = false;
        let hasCartao = false;

        if (dadosRecebimento?.dadosRecebimentoItens) {
            for (const item of dadosRecebimento.dadosRecebimentoItens) {
                if (item.dadosCheque) {
                    hasCheque = true;
                } else {
                    hasCartao = true;
                }
                if (hasCheque && hasCartao) break;
            }
        }

        if (hasCheque && !hasCartao) return "Recebimentos em cheque";
        if (!hasCheque && hasCartao) return "Contas receber baixados na origem";
        return "Contas receber baixados na origem e recebimentos em cheque";
    }, []);
    const possuiDadosCheque = useCallback((dadosCheque: FlowContaReceberDadosChequeDTO | null | undefined) => {
        return !!dadosCheque
    }, [])

    const possuiDadosRecebimento = useCallback((visualizar: FlowContaReceberVisualizacaoDTO | null) => {
        return (!isEmpty(visualizar?.dadosRecebimento?.dadosRecebimentoItens ?? []) || !isEmpty(visualizar?.dadosRecebimento?.recebimentos ?? []))
    }, [])

    const possuiDadosBaixa = (dadosBaixa: Array<FlowContaReceberBaixa> | undefined) => {
        return !isEmpty(dadosBaixa)
    }

    const onToggle = useCallback((colapsadas: Array<string>, uuid: string) => {
        const newColapsadas = colapsadas.includes(uuid)
            ? colapsadas.filter(e => e !== uuid)
            : [...colapsadas, uuid];

        setStateLocal(prevState => ({
            ...prevState,
            colapsadas: newColapsadas
        }))
    }, [])

    const renderDadosRecebimentoItens = useCallback((arr: Array<FlowContaReceberDadosRecebimentoItensDTO>, colapsadas: Array<string>) => {
        return arr.map(item => {
            return (
                <CustomTableCollapsable
                    colapsadas={colapsadas}
                    onToggle={onToggle}
                    item={item}
                    columns={dadosRecebimentoItensColumns}
                    columnsChildren={[]}
                    children={item.dadosCheque}
                    toFilter="dadosCheque"
                />
            )
        })
        //eslint-disable-next-line
    }, [])

    const dadosRecebimentoItensColumns: Array<ColumnData<FlowContaReceberDadosRecebimentoItensDTO>> =
        [
            {
                id: "recebimento-descricao",
                header: "Descricão",
                key: "descricao",
                type: "string",
                width: "50%",
                textAlign: "left"
            },
            {
                id: "recebimento-parcela",
                header: "Parcela",
                key: "parcela",
                type: "string",
                width: "10%",
                textAlign: "center"
            },
            {
                id: "recebimento-emissao",
                header: "Emissão",
                key: "emissao",
                type: "string",
                width: "10%",
                textAlign: "center"
            },
            {
                id: "recebimento-vencimento",
                header: "Vencimento",
                key: "vencimento",
                type: "string",
                width: "10%",
                textAlign: "center"
            },
            {
                id: "recebimento-valor",
                header: "Valor",
                key: "valor",
                type: "string",
                width: "10%",
                textAlign: "center"
            },
            {
                id: "recebimento-status",
                header: "Status",
                key: "status",
                type: "string",
                width: "10%",
                textAlign: "center"
            },
        ]

    const cartaoRecebimentoColumns: Array<ColumnData<FlowContaReceberCartaoDTO>> =
        [
            {
                id: "recebimento-descricao",
                header: "Credenciadora",
                key: "credenciadora",
                type: "string",
                width: "30%",
                textAlign: "left"
            },
            {
                id: "recebimento-descricao",
                header: "Bandeira",
                key: "bandeira",
                type: "string",
                width: "10%",
                textAlign: "center"
            },
            {
                id: "recebimento-descricao",
                header: "Modo de pagamento",
                key: "modoPagamento",
                type: "string",
                width: "12%",
                textAlign: "center"
            },
            {
                id: "recebimento-descricao",
                header: "Nsu",
                key: "nsu",
                type: "string",
                width: "5%",
                textAlign: "center"
            },
            {
                id: "recebimento-descricao",
                header: "Número de controle",
                key: "numeroControle",
                type: "string",
                width: "12%",
                textAlign: "center"
            },
            {
                id: "recebimento-descricao",
                header: "Bandeira",
                key: "bandeira",
                type: "string",
                width: "10%",
                textAlign: "center"
            },
            {
                id: "recebimento-descricao",
                header: "Data",
                key: "data",
                type: "string",
                width: "5%",
                textAlign: "center"
            },
            {
                id: "recebimento-descricao",
                header: "Valor",
                key: "valor",
                type: "string",
                width: "10%",
                textAlign: "right"
            },
        ]


    const renderRecebimentos = useCallback((arr: Array<FlowContaReceberCartaoDTO>, colapsadas: Array<string>) => {
        return arr.map(item => {
            return (
                <CustomTableCollapsable
                    colapsadas={colapsadas}
                    onToggle={onToggle}
                    item={item}
                    columns={cartaoRecebimentoColumns}
                    columnsChildren={dadosRecebimentoItensColumns}
                    childrenTitle="Contas a receber geradas"
                    children={item.contasGeradas}
                    toFilter="contasGeradas"
                />
            )
        })
        //eslint-disable-next-line
    }, [])




    return (
        <CustomDialog
            open
            fullWidth
            maxWidth={'xl'}
            sx={{
                maxWidth: "90vw"
            }}
            onClose={props.onClose}
        >
            <DialogTitle
                id="dialog-title"
                textAlign={'center'} sx={{
                    padding: 0
                }}>
                <HeaderOuBannerDependendoDaInterpretacaoH1
                    id='visualizacao_conta_receber'
                    iconClassName='fa-solid fa-eye'
                    label='Visualização da Parcela'
                />
            </DialogTitle>

            <Grid sx={{
                textAlign: "center",
            }}>
                {!stateLocal.isLoading &&
                    <TabContext value={stateLocal.tipoRender}>
                        <TabList
                            sx={{
                                width: '100%',
                                '& .MuiTabs-scroller .MuiTabs-indicator': {
                                    backgroundColor: theme.palette.primary.main,
                                },
                                '& .MuiTabs-flexContainer': {
                                    display: 'block',
                                    flexWrap: 'wrap',
                                },
                                marginRight: "5px",
                                '& > div': {
                                    overflowX: "auto !important"
                                },
                                padding: 0,
                                margin: 0,
                                border: 0
                            }}
                        >
                            <Tab
                                disabled={stateLocal.isLoading}
                                sx={[tabHeaderStyle, { height: "60px", textTransform: "none", padding: "4px 10px" }]}
                                onClick={() => onSetTipoTela(VisualizarContaReceberRender.IDENTIFICACAO)}
                                label={captalizeString(VisualizarContaReceberRender.IDENTIFICACAO)}
                                value={VisualizarContaReceberRender.IDENTIFICACAO}
                            />
                            <Tab
                                disabled={stateLocal.isLoading}
                                sx={[tabHeaderStyle, { height: "60px", textTransform: "none", padding: "4px 10px" }]}
                                onClick={() => onSetTipoTela(VisualizarContaReceberRender.DADOS_COBRANCA)}
                                label={captalizeString(VisualizarContaReceberRender.DADOS_COBRANCA)}
                                value={VisualizarContaReceberRender.DADOS_COBRANCA}
                            />
                            {possuiDadosCheque(stateLocal.flowContaReceberVisualizarDTO?.dadosCheque) && (
                                <Tab
                                    disabled={stateLocal.isLoading}
                                    sx={[tabHeaderStyle, { height: "60px", textTransform: "none", padding: "4px 10px" }]}
                                    onClick={() => onSetTipoTela(VisualizarContaReceberRender.DADOS_CHEQUE)}
                                    label={captalizeString(VisualizarContaReceberRender.DADOS_CHEQUE)}
                                    value={VisualizarContaReceberRender.DADOS_CHEQUE}
                                />
                            )}
                            {possuiDadosBaixa(stateLocal.flowContaReceberVisualizarDTO?.dadosBaixa) && (
                                <Tab
                                    disabled={stateLocal.isLoading}
                                    sx={[tabHeaderStyle, { height: "60px", textTransform: "none", padding: "4px 10px" }]}
                                    onClick={() => onSetTipoTela(VisualizarContaReceberRender.DADOS_BAIXA)}
                                    label={captalizeString(VisualizarContaReceberRender.DADOS_BAIXA)}
                                    value={VisualizarContaReceberRender.DADOS_BAIXA}
                                />
                            )}
                            {possuiDadosRecebimento(stateLocal.flowContaReceberVisualizarDTO) && (
                                <Tab
                                    disabled={stateLocal.isLoading}
                                    sx={[tabHeaderStyle, { height: "60px", textTransform: "none", padding: "4px 10px" }]}
                                    onClick={() => onSetTipoTela(VisualizarContaReceberRender.DADOS_RECEBIMENTO)}
                                    label={captalizeString(VisualizarContaReceberRender.DADOS_RECEBIMENTO)}
                                    value={VisualizarContaReceberRender.DADOS_RECEBIMENTO}
                                />
                            )}
                            <Tab
                                disabled={stateLocal.isLoading}
                                sx={[tabHeaderStyle, { letterSpacing: 2, height: "60px", textTransform: "none" }]}
                                onClick={() => onSetTipoTela(VisualizarContaReceberRender.HISTORICO_MOVIMENTACAO)}
                                label={captalizeString(VisualizarContaReceberRender.HISTORICO_MOVIMENTACAO)}
                                value={VisualizarContaReceberRender.HISTORICO_MOVIMENTACAO}
                            />
                            <Tab
                                disabled={stateLocal.isLoading}
                                sx={[tabHeaderStyle, { height: "60px", textTransform: "none", padding: "4px 10px" }]}
                                onClick={() => onSetTipoTela(VisualizarContaReceberRender.REPRESENTANTES)}
                                label={captalizeString(VisualizarContaReceberRender.REPRESENTANTES)}
                                value={VisualizarContaReceberRender.REPRESENTANTES}
                            />
                            <Tab
                                disabled={stateLocal.isLoading}
                                sx={[tabHeaderStyle, { height: "60px", textTransform: "none", padding: "4px 10px" }]}
                                onClick={() => onSetTipoTela(VisualizarContaReceberRender.HISTORICO_ALTERACAO_VENCIMENTO)}
                                label={captalizeString(VisualizarContaReceberRender.HISTORICO_ALTERACAO_VENCIMENTO)}
                                value={VisualizarContaReceberRender.HISTORICO_ALTERACAO_VENCIMENTO}
                            />
                            <Tab
                                disabled={stateLocal.isLoading}
                                sx={[tabHeaderStyle, { height: "60px", textTransform: "none", padding: "4px 10px" }]}
                                onClick={() => onSetTipoTela(VisualizarContaReceberRender.CONTAS_APROPRIACOES)}
                                label={captalizeString(VisualizarContaReceberRender.CONTAS_APROPRIACOES)}
                                value={VisualizarContaReceberRender.CONTAS_APROPRIACOES}
                            />
                            <Tab
                                disabled={stateLocal.isLoading}
                                sx={[tabHeaderStyle, { height: "60px", textTransform: "none", padding: "4px 10px" }]}
                                onClick={() => onSetTipoTela(VisualizarContaReceberRender.OBSERVACAO)}
                                label={captalizeString(VisualizarContaReceberRender.OBSERVACAO)}
                                value={VisualizarContaReceberRender.OBSERVACAO}
                            />
                        </TabList>
                    </TabContext>
                }
            </Grid>

            <DialogContent id="dialog-content"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '15px',
                    alignItems: 'center',
                    overflowX: 'hidden'
                }}>

                <Grid height="1000px" width={"100%"} >
                    {isDadosBaixa() &&
                        <Box sx={{ justifyContent: "center", display: "flex", width: "100%", marginY: "10px", height: "30px" }}>
                            <Typography sx={{ color: theme.palette.primary.main, fontWeight: "bold" }}>
                                Baixas
                            </Typography>
                        </Box>
                    }

                    {isDadosRecebimento() &&
                        <Box sx={{ justifyContent: "center", display: "flex", width: "100%", marginY: "10px", height: "30px" }}>
                            <Typography sx={{ color: theme.palette.primary.main, fontWeight: "bold" }}>
                                {getContasReceberDadosRecebimentoMessage(stateLocal.flowContaReceberVisualizarDTO?.dadosRecebimento)}
                            </Typography>
                        </Box>
                    }

                    <TableContainer sx={{ maxHeight: '500px', overflowY: 'auto' }}>
                        {isIdentificacaoRender() && (
                            <Table sx={{
                                border: `2px solid ${theme.palette.primary.main}`,
                                display: stateLocal.isLoading ? "flex" : "table",
                                justifyContent: stateLocal.isLoading ? "center" : "normal",
                                alignItems: stateLocal.isLoading ? "center" : "normal",
                                height: stateLocal.isLoading ? "400px" : undefined
                            }}>
                                {stateLocal.isLoading ?
                                    <CircularProgress
                                        size={48}
                                        color='primary'
                                        sx={{
                                            top: '50%',
                                            left: '50%',
                                        }}
                                    />
                                    :
                                    <>
                                        <VisualizarItem title="Descrição" item={stateLocal.flowContaReceberVisualizarDTO?.identificacao.descricao} marginTop="20px" />
                                        <VisualizarItem title="Pessoa" item={stateLocal.flowContaReceberVisualizarDTO?.identificacao.pessoa} />
                                        <VisualizarItem title="Número do documento" item={stateLocal.flowContaReceberVisualizarDTO?.identificacao.numeroDocumento} />
                                        <VisualizarItem title="Parcela" item={stateLocal.flowContaReceberVisualizarDTO?.identificacao.parcela} />
                                        <VisualizarItem title="Forma de pagamento" item={stateLocal.flowContaReceberVisualizarDTO?.identificacao.formaPagamento} />
                                        <VisualizarItem title="Tipo" item={stateLocal.flowContaReceberVisualizarDTO?.identificacao.tipo} />
                                        <VisualizarItem title="Status da parcela" item={stateLocal.flowContaReceberVisualizarDTO?.identificacao.status} />
                                        <VisualizarItem title="Emissão" item={stateLocal.flowContaReceberVisualizarDTO?.identificacao.emissao} />
                                        <VisualizarItem title="Vencimento" item={stateLocal.flowContaReceberVisualizarDTO?.identificacao.vencimento} />
                                        <VisualizarItem title="Valor" item={!isEmpty(stateLocal.flowContaReceberVisualizarDTO?.identificacao.descricao) ? currencyOf(stateLocal.flowContaReceberVisualizarDTO?.identificacao.valor) : ""} />
                                        <VisualizarItem title="Valor em aberto" item={!isEmpty(stateLocal.flowContaReceberVisualizarDTO?.identificacao.descricao) ? currencyOf(stateLocal.flowContaReceberVisualizarDTO?.identificacao.valorAberto) : ""} />
                                    </>
                                }

                            </Table>
                        )}

                        {isDadosCobrancaRender() && (
                            <Table sx={{
                                border: `2px solid ${theme.palette.primary.main}`,
                                display: stateLocal.isLoading ? "flex" : "table",
                                justifyContent: stateLocal.isLoading ? "center" : "normal",
                                alignItems: stateLocal.isLoading ? "center" : "normal",
                            }}>
                                <VisualizarItem title="Posição de cobrança" item={stateLocal.flowContaReceberVisualizarDTO?.dadosCobranca.posicaoCobranca} marginTop="20px" />
                                <VisualizarItem title="Situação do boleto" item={stateLocal.flowContaReceberVisualizarDTO?.dadosCobranca.situacaoBoleto} />
                                <VisualizarItem title="Situação de cobrança" item={stateLocal.flowContaReceberVisualizarDTO?.dadosCobranca.situacaoCobranca} />
                                <VisualizarItem title="Cobrança bancária" item={stateLocal.flowContaReceberVisualizarDTO?.dadosCobranca.cobrancaBancaria} />
                                <VisualizarItem title="Modalidade" item={stateLocal.flowContaReceberVisualizarDTO?.dadosCobranca.modalidade} />
                                <VisualizarItem title="Conta corrente" item={stateLocal.flowContaReceberVisualizarDTO?.dadosCobranca.contaCorrente} />
                                <VisualizarItem title="Nosso número" item={stateLocal.flowContaReceberVisualizarDTO?.dadosCobranca.nossoNumero} />
                            </Table>
                        )}


                        {isDadosCheque() && stateLocal.flowContaReceberVisualizarDTO?.dadosCheque && (
                            <Table sx={{
                                border: `2px solid ${theme.palette.primary.main}`,
                                display: "table",
                                height: "350px"
                            }}>
                                <VisualizarItem title="Conta financeira origem" item={stateLocal.flowContaReceberVisualizarDTO?.dadosCheque.contaFinanceiraOrigem} marginTop="20px" />
                                <VisualizarItem title="Conta financeira atual" item={stateLocal.flowContaReceberVisualizarDTO?.dadosCheque.contaFinanceiraAtual} />
                                <VisualizarItem title="Banco" item={stateLocal.flowContaReceberVisualizarDTO?.dadosCheque.banco} />
                                <VisualizarItem title="Agência" item={stateLocal.flowContaReceberVisualizarDTO?.dadosCheque.agencia} />
                                <VisualizarItem title="Número" item={stateLocal.flowContaReceberVisualizarDTO?.dadosCheque.numero} />
                                <VisualizarItem title="Emitente" item={stateLocal.flowContaReceberVisualizarDTO?.dadosCheque.emitente} />
                                <VisualizarItem title="Situação" item={stateLocal.flowContaReceberVisualizarDTO?.dadosCheque.situacao} />
                                <VisualizarItem title="Data de recebimento" item={stateLocal.flowContaReceberVisualizarDTO?.dadosCheque.dataRecebimento} />
                            </Table>
                        )}

                        {isDadosBaixa() &&
                            <Table stickyHeader sx={{
                                border: `2px solid ${theme.palette.primary.main}`,
                            }} >
                                <TableBody sx={{ display: "table-row" }}>
                                    <TableRow sx={{
                                        width: "100%",
                                        padding: 0,
                                        bgcolor: theme.palette.background.default,
                                    }}>
                                        <TableCell sx={{ width: "10%", fontWeight: "bold" }} align="center">
                                            {TDadosBaixaHeader.DATA_BAIXA}
                                        </TableCell>
                                        <TableCell sx={{ width: "42%", fontWeight: "bold" }} align="left">
                                            {TDadosBaixaHeader.CONTA_FINANCEIRA}
                                        </TableCell>
                                        <TableCell sx={{ width: "15%", fontWeight: "bold" }} align="center">
                                            {TDadosBaixaHeader.FORMA_PAGAMENTO}
                                        </TableCell>
                                        <TableCell sx={{ width: "7.5%", fontWeight: "bold" }} align="center">
                                            {TDadosBaixaHeader.VALOR}
                                        </TableCell>
                                        <TableCell sx={{ width: "5.5%", paddingRight: "20px", fontWeight: "bold" }} align="right">
                                            {TDadosBaixaHeader.MULTA}
                                        </TableCell>
                                        <TableCell sx={{ width: "5.5%", fontWeight: "bold" }} align="center">
                                            {TDadosBaixaHeader.JUROS}
                                        </TableCell>
                                        <TableCell sx={{ width: "5.5%", fontWeight: "bold" }} align="center">
                                            {TDadosBaixaHeader.DESCONTO}
                                        </TableCell>
                                        <TableCell sx={{ width: "9%", fontWeight: "bold" }} align="center">
                                            {TDadosBaixaHeader.TOTAL}
                                        </TableCell>
                                    </TableRow>
                                    {stateLocal.flowContaReceberVisualizarDTO?.dadosBaixa.map((e) => {
                                        return (
                                            <TableRow sx={{
                                                borderBottom: `1px solid ${theme.palette.text.disabled}`,
                                                alignItems: "center"
                                            }}>
                                                <TableCell sx={{
                                                    width: "10%",
                                                    padding: "5px",
                                                    paddingLeft: "15px",
                                                }} align="center">
                                                    {e.dataBaixa}
                                                </TableCell>

                                                <TableCell sx={{
                                                    width: "42%",
                                                    padding: "5px",
                                                    paddingLeft: "15px",
                                                }}>
                                                    {e.contaFinanceira}
                                                </TableCell>
                                                <TableCell sx={{
                                                    width: "15%",
                                                    padding: "5px",
                                                    paddingLeft: "15px",
                                                }}
                                                    align="center"
                                                >
                                                    {e.formaPagamento}
                                                </TableCell>

                                                <TableCell sx={{
                                                    width: "9.5%",
                                                    padding: "5px",
                                                    paddingLeft: "15px"
                                                }}
                                                    align="center"
                                                >
                                                    {e.valor}
                                                </TableCell>

                                                <TableCell sx={{
                                                    width: "5.5%",
                                                    padding: "5px",
                                                    paddingLeft: "15px",
                                                }}
                                                    align="center"
                                                >
                                                    {e.multa}
                                                </TableCell>

                                                <TableCell sx={{
                                                    width: "5.5%",
                                                    padding: "5px",
                                                    paddingLeft: "15px",
                                                }}
                                                    align="center"
                                                >
                                                    {e.juros}
                                                </TableCell>

                                                <TableCell sx={{
                                                    width: "5.5%",
                                                    padding: "5px",
                                                    paddingLeft: "15px"
                                                }}
                                                    align="center"
                                                >
                                                    {e.desconto}
                                                </TableCell>

                                                <TableCell sx={{
                                                    width: "9%",
                                                    padding: "5px",
                                                }}
                                                    align="center"
                                                >
                                                    {e.total}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        }


                        {isDadosRecebimento() && (
                            <Table sx={{
                                border: `2px solid ${theme.palette.primary.main}`,
                                width: "100%",
                            }}>
                                {stateLocal.flowContaReceberVisualizarDTO?.dadosRecebimento?.dadosRecebimentoItens &&
                                    <>
                                        <TableRow sx={{
                                            width: "100%",
                                            display: "inline-table",
                                        }}>
                                            {dadosRecebimentoItensColumns.map((item) => {
                                                return (
                                                    <TableCell sx={{
                                                        width: item.width,
                                                        minWidth: item.width,
                                                        maxWidth: item.width,
                                                        fontWeight: "bold",
                                                        paddingY: 0,
                                                        bgcolor: theme.palette.background.default,
                                                    }} align={item.textAlign}>
                                                        {item.header}
                                                    </TableCell>
                                                )
                                            })}
                                        </TableRow>
                                        {renderDadosRecebimentoItens(stateLocal.flowContaReceberVisualizarDTO?.dadosRecebimento?.dadosRecebimentoItens ?? [], stateLocal.colapsadas)}
                                    </>
                                }
                                {!!stateLocal.flowContaReceberVisualizarDTO?.dadosRecebimento?.dadosRecebimentoCartao &&
                                    <Box>
                                        <VisualizarItem title="Credenciadora" item={stateLocal.flowContaReceberVisualizarDTO.dadosRecebimento.dadosRecebimentoCartao.credenciadora} />
                                        <VisualizarItem title="Bandeira" item={stateLocal.flowContaReceberVisualizarDTO.dadosRecebimento.dadosRecebimentoCartao.bandeira} />
                                        <VisualizarItem title="Modo de pagamento" item={stateLocal.flowContaReceberVisualizarDTO.dadosRecebimento.dadosRecebimentoCartao.modoPagamento} />
                                        <VisualizarItem title="Nsu" item={stateLocal.flowContaReceberVisualizarDTO.dadosRecebimento.dadosRecebimentoCartao.nsu} />
                                        <VisualizarItem title="Número de controle" item={stateLocal.flowContaReceberVisualizarDTO.dadosRecebimento.dadosRecebimentoCartao.numeroControle} />
                                        <VisualizarItem title="Código de autorização" item={stateLocal.flowContaReceberVisualizarDTO.dadosRecebimento.dadosRecebimentoCartao.codigoAutorizacao} />
                                        <VisualizarItem title="Número do terminal" item={stateLocal.flowContaReceberVisualizarDTO.dadosRecebimento.dadosRecebimentoCartao.numeroTerminal} />
                                        <VisualizarItem title="Situação" item={stateLocal.flowContaReceberVisualizarDTO.dadosRecebimento.dadosRecebimentoCartao.situacao} />
                                        <VisualizarItem title="Data de recebimento" item={stateLocal.flowContaReceberVisualizarDTO.dadosRecebimento.dadosRecebimentoCartao.dataRecebimento} />
                                        <VisualizarItem title="Valor" item={stateLocal.flowContaReceberVisualizarDTO.dadosRecebimento.dadosRecebimentoCartao.valor} />
                                    </Box>
                                }
                                {stateLocal.flowContaReceberVisualizarDTO &&
                                    stateLocal.flowContaReceberVisualizarDTO?.dadosRecebimento &&
                                    !isEmpty(stateLocal.flowContaReceberVisualizarDTO?.dadosRecebimento?.recebimentos) &&
                                    <>
                                        <TableRow sx={{
                                            width: "100%",
                                            display: "inline-table",
                                        }}>
                                            {cartaoRecebimentoColumns.map((item) => {
                                                return (
                                                    <TableCell sx={{
                                                        width: item.width,
                                                        minWidth: item.width,
                                                        maxWidth: item.width,
                                                        fontWeight: "bold",
                                                        paddingY: 0,
                                                        bgcolor: theme.palette.background.default,
                                                    }} align={item.textAlign}
                                                    >
                                                        {item.header}
                                                    </TableCell>
                                                )
                                            })}
                                        </TableRow>
                                        {renderRecebimentos(stateLocal.flowContaReceberVisualizarDTO.dadosRecebimento.recebimentos, stateLocal.colapsadas)}
                                    </>
                                }
                            </Table>
                        )}

                        {isHistoricoMovimentacaoRender() && (
                            <Table sx={{
                                border: `2px solid ${theme.palette.primary.main}`,
                                display: "table",
                            }}>
                                {isEmpty(stateLocal.flowContaReceberVisualizarDTO?.historicoMovimentacoes.historicos) && (
                                    <Typography sx={{
                                        width: '100%',
                                        textAlign: "center",
                                        marginTop: "30px",
                                        fontSize: "30px",
                                        color: theme.palette.text.disabled,
                                        alignSelf: "center",
                                        userSelect: "none",
                                    }}>
                                        Nenhum histórico de movimentação encontrado.
                                    </Typography>
                                )}

                                {!isEmpty(stateLocal.flowContaReceberVisualizarDTO?.historicoMovimentacoes.historicos) && (
                                    <TableHead sx={{
                                        width: '100%',
                                        borderBottom: `1px solid ${theme.palette.text.disabled}`,
                                        maxHeight: "55px",
                                        bgcolor: theme.palette.background.default,
                                    }}>
                                        <TableRow sx={{
                                            border: 0,
                                            display: "flex"
                                        }}>
                                            <TableCell sx={{
                                                width: "5%",
                                                fontWeight: "bold",
                                                border: 0,
                                                padding: "5px",
                                                paddingLeft: "15px",
                                            }}>
                                                Lançamento
                                            </TableCell>

                                            <TableCell sx={{
                                                width: "25%",
                                                fontWeight: "bold",
                                                border: 0,
                                                padding: "5px",
                                                paddingLeft: "15px",
                                            }}>
                                                Tipo de movimento
                                            </TableCell>

                                            <TableCell sx={{
                                                width: "10%",
                                                fontWeight: "bold",
                                                border: 0,
                                                padding: "5px",
                                                paddingLeft: "15px"
                                            }}>
                                                Valor Recebido
                                            </TableCell>

                                            <TableCell sx={{
                                                width: "7%",
                                                fontWeight: "bold",
                                                border: 0,
                                                padding: "5px",
                                                paddingLeft: "15px"
                                            }}>
                                                Multa
                                            </TableCell>

                                            <TableCell sx={{
                                                width: "7%",
                                                fontWeight: "bold",
                                                border: 0,
                                                padding: "5px",
                                                paddingLeft: "15px"
                                            }}>
                                                Juros
                                            </TableCell>

                                            <TableCell sx={{
                                                width: "7%",
                                                fontWeight: "bold",
                                                border: 0,
                                                padding: "5px",
                                                paddingLeft: "15px"
                                            }}>
                                                Desconto
                                            </TableCell>

                                            <TableCell sx={{
                                                width: "7%",
                                                fontWeight: "bold",
                                                border: 0,
                                                padding: "5px",
                                                paddingLeft: "15px"
                                            }}>
                                                Tarifa
                                            </TableCell>

                                            <TableCell sx={{
                                                width: "7%",
                                                fontWeight: "bold",
                                                border: 0,
                                                padding: "5px",
                                                paddingLeft: "15px"
                                            }}>
                                                Total
                                            </TableCell>

                                            <TableCell sx={{
                                                width: "8%",
                                                fontWeight: "bold",
                                                border: 0,
                                                padding: "5px",
                                                paddingLeft: "15px"
                                            }}>
                                                Usuário
                                            </TableCell>

                                            <TableCell sx={{
                                                width: "10%",
                                                fontWeight: "bold",
                                                border: 0,
                                                padding: "5px",
                                                paddingLeft: "15px"
                                            }}>
                                                Data
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                )}

                                {!isEmpty(stateLocal.flowContaReceberVisualizarDTO?.historicoMovimentacoes.historicos) && (
                                    stateLocal.flowContaReceberVisualizarDTO?.historicoMovimentacoes.historicos.map((e, index, arr) => {
                                        return (
                                            <TableRow sx={{
                                                display: "flex",
                                                borderBottom: index === (arr.length - 1) ? 0 : `1px solid ${theme.palette.text.disabled}`,
                                                alignItems: "center"
                                            }}>
                                                <TableCell sx={{
                                                    width: "5%",
                                                    border: 0,
                                                    padding: "5px",
                                                    paddingLeft: "15px",
                                                }}>
                                                    {e.lancamento}
                                                </TableCell>

                                                <Tooltip title={e.tipoMovimento}>
                                                    <TableCell sx={{
                                                        width: "25%",
                                                        border: 0,
                                                        padding: "5px",
                                                        paddingLeft: "15px",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        maxWidth: "25%"
                                                    }}>
                                                        {e.tipoMovimento}
                                                    </TableCell>
                                                </Tooltip>


                                                <TableCell sx={{
                                                    width: "10%",
                                                    border: 0,
                                                    padding: "5px",
                                                    paddingLeft: "15px"
                                                }}>
                                                    {currencyOf(e.valor)}
                                                </TableCell>

                                                <TableCell sx={{
                                                    width: "7%",
                                                    border: 0,
                                                    padding: "5px",
                                                    paddingLeft: "15px"
                                                }}>
                                                    {currencyOf(e.multa)}
                                                </TableCell>

                                                <TableCell sx={{
                                                    width: "7%",
                                                    border: 0,
                                                    padding: "5px",
                                                    paddingLeft: "15px"
                                                }}>
                                                    {currencyOf(e.juros)}
                                                </TableCell>

                                                <TableCell sx={{
                                                    width: "7%",
                                                    border: 0,
                                                    padding: "5px",
                                                    paddingLeft: "15px"
                                                }}>
                                                    {currencyOf(e.desconto)}
                                                </TableCell>

                                                <TableCell sx={{
                                                    width: "7%",
                                                    border: 0,
                                                    padding: "5px",
                                                    paddingLeft: "15px"
                                                }}>
                                                    {currencyOf(e.tarifa)}
                                                </TableCell>

                                                <TableCell sx={{
                                                    width: "7%",
                                                    border: 0,
                                                    padding: "5px",
                                                    paddingLeft: "15px"
                                                }}>
                                                    {currencyOf(e.total)}
                                                </TableCell>

                                                <TableCell sx={{
                                                    width: "8%",
                                                    border: 0,
                                                    padding: "5px",
                                                    paddingLeft: "15px"
                                                }}>
                                                    {e.usuario}
                                                </TableCell>

                                                <TableCell sx={{
                                                    width: "10%",
                                                    border: 0,
                                                    padding: "5px",
                                                    paddingLeft: "15px"
                                                }}>
                                                    {e.data}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })
                                )}
                            </Table>
                        )}

                        {isRepresentantesRender() && (
                            <Table sx={{
                                border: `2px solid ${theme.palette.primary.main}`,
                                display: "table",
                            }}>
                                {!isEmpty(stateLocal.flowContaReceberVisualizarDTO?.representantes.representante) && (
                                    <>
                                        <VisualizarItem title="Representante" item={stateLocal.flowContaReceberVisualizarDTO?.representantes.representante} />
                                        <VisualizarItem title="Forma de comissão" item={stateLocal.flowContaReceberVisualizarDTO?.representantes.formaComissao} />
                                    </>
                                )}
                                {isEmpty(stateLocal.flowContaReceberVisualizarDTO?.representantes.representante) && (
                                    <Typography sx={{
                                        width: '100%',
                                        textAlign: "center",
                                        fontSize: "30px",
                                        color: theme.palette.text.disabled,
                                        alignSelf: "center",
                                        userSelect: "none",
                                    }}>
                                        Nenhum representante informado.
                                    </Typography>
                                )}

                                {!isEmpty(stateLocal.flowContaReceberVisualizarDTO?.representantes.outrosComissionados) &&
                                    <Typography sx={{
                                        textAlign: "center",
                                        marginTop: "20px",
                                        fontSize: "20px",
                                        fontWeight: "bold"
                                    }}>
                                        Outros comissionados
                                    </Typography>
                                }
                                {!isEmpty(stateLocal.flowContaReceberVisualizarDTO?.representantes.outrosComissionados) && (
                                    <Table style={{
                                        marginTop: "20px"
                                    }}>
                                        <TableHead sx={{
                                            width: '100%',
                                            borderTop: `1px solid ${theme.palette.text.disabled}`,
                                            borderBottom: `1px solid ${theme.palette.text.disabled}`,
                                            maxHeight: "55px",
                                            bgcolor: theme.palette.background.default
                                        }}>
                                            <TableRow sx={{
                                                border: 0,
                                                display: "flex"
                                            }}>
                                                <TableCell sx={{
                                                    width: "50%",
                                                    fontWeight: "bold",
                                                    border: 0,
                                                    padding: "5px",
                                                    paddingLeft: "15px",
                                                }}>
                                                    Representante
                                                </TableCell>
                                                <TableCell sx={{
                                                    width: "30%",
                                                    fontWeight: "bold",
                                                    border: 0,
                                                    padding: "5px",
                                                }}>
                                                    Forma de comissão
                                                </TableCell>

                                                <TableCell sx={{
                                                    width: "20%",
                                                    fontWeight: "bold",
                                                    border: 0,
                                                    padding: "5px",
                                                }}>
                                                    Papel
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>

                                        {stateLocal.flowContaReceberVisualizarDTO?.representantes.outrosComissionados.map(e => {
                                            return (
                                                <TableRow sx={{
                                                    display: "flex",
                                                    borderBottom: `1px solid ${theme.palette.text.disabled}`
                                                }}>
                                                    <TableCell sx={{
                                                        width: "50%",
                                                        border: 0,
                                                        padding: "5px",
                                                        paddingLeft: "15px"
                                                    }}>
                                                        {e.representante}
                                                    </TableCell>

                                                    <TableCell sx={{
                                                        width: "30%",
                                                        border: 0,
                                                        padding: "5px",
                                                        paddingLeft: "15px"
                                                    }}>
                                                        {e.formaComissao}
                                                    </TableCell>

                                                    <TableCell sx={{
                                                        width: "20%",
                                                        border: 0,
                                                        padding: "5px",
                                                        paddingLeft: "15px"
                                                    }}>
                                                        {e.papel}
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}

                                    </Table>
                                )}
                            </Table>
                        )}

                        {isHistoricoAlteracaoVencimentoRender() && (
                            <Table sx={{
                                border: `2px solid ${theme.palette.primary.main}`,
                                display: isEmpty(stateLocal.flowContaReceberVisualizarDTO?.historicoAlteracaoVencimentos.historicos) ? "flex" : "table",
                                maxHeight: "350px"
                            }}>
                                {isEmpty(stateLocal.flowContaReceberVisualizarDTO?.historicoAlteracaoVencimentos.historicos) && (
                                    <Typography sx={{
                                        width: '100%',
                                        textAlign: "center",
                                        fontSize: "30px",
                                        color: theme.palette.text.disabled,
                                        alignSelf: "center",
                                        userSelect: "none",
                                    }}>
                                        Nenhum histórico de alterações de vencimento.
                                    </Typography>
                                )}

                                {!isEmpty(stateLocal.flowContaReceberVisualizarDTO?.historicoAlteracaoVencimentos.historicos) && (
                                    <TableHead sx={{
                                        width: '100%',
                                        borderBottom: `1px solid ${theme.palette.text.disabled}`,
                                        maxHeight: "55px",
                                        bgcolor: theme.palette.background.default
                                    }}>
                                        <TableRow sx={{
                                            border: 0,
                                            display: "flex"
                                        }}>
                                            <TableCell sx={{
                                                width: "50%",
                                                fontWeight: "bold",
                                                border: 0,
                                                padding: "5px",
                                                paddingLeft: "15px",
                                            }}>
                                                Tipo
                                            </TableCell>

                                            <TableCell sx={{
                                                width: "12.5%",
                                                fontWeight: "bold",
                                                border: 0,
                                                padding: "5px",
                                                paddingLeft: "15px",
                                            }}>
                                                Vencimento anterior
                                            </TableCell>

                                            <TableCell sx={{
                                                width: "12.5%",
                                                fontWeight: "bold",
                                                border: 0,
                                                padding: "5px",
                                                paddingLeft: "15px",
                                            }}>
                                                Novo vencimento
                                            </TableCell>


                                            <TableCell sx={{
                                                width: "12.5%",
                                                fontWeight: "bold",
                                                border: 0,
                                                padding: "5px",
                                                paddingLeft: "15px",
                                            }}>
                                                Usuário
                                            </TableCell>


                                            <TableCell sx={{
                                                width: "12.5%",
                                                fontWeight: "bold",
                                                border: 0,
                                                padding: "5px",
                                                paddingLeft: "15px",
                                            }}>
                                                Data
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                )}

                                {!isEmpty(stateLocal.flowContaReceberVisualizarDTO?.historicoAlteracaoVencimentos.historicos) && (
                                    stateLocal.flowContaReceberVisualizarDTO?.historicoAlteracaoVencimentos.historicos.map(e => {
                                        return (
                                            <TableRow sx={{
                                                display: "flex",
                                                borderBottom: `1px solid ${theme.palette.text.disabled}`,
                                                alignItems: "center"
                                            }}>
                                                <TableCell sx={{
                                                    width: "50%",
                                                    border: 0,
                                                    padding: "5px",
                                                    paddingLeft: "15px",
                                                }}>
                                                    {e.tipo}
                                                </TableCell>

                                                <TableCell sx={{
                                                    width: "12.5%",
                                                    border: 0,
                                                    padding: "5px",
                                                    paddingLeft: "15px"
                                                }}>
                                                    {e.vencimentoAnterior}
                                                </TableCell>

                                                <TableCell sx={{
                                                    width: "12.5%",
                                                    border: 0,
                                                    padding: "5px",
                                                    paddingLeft: "15px"
                                                }}>
                                                    {e.vencimentoNovo}
                                                </TableCell>

                                                <TableCell sx={{
                                                    width: "12.5%",
                                                    border: 0,
                                                    padding: "5px",
                                                    paddingLeft: "15px"
                                                }}>
                                                    {e.usuario}
                                                </TableCell>

                                                <TableCell sx={{
                                                    width: "12.5%",
                                                    border: 0,
                                                    padding: "5px",
                                                    paddingLeft: "15px"
                                                }}>
                                                    {e.data}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })
                                )}
                            </Table>
                        )}

                        {isContasApropriacoesRender() && (
                            <Table sx={{
                                border: `2px solid ${theme.palette.primary.main}`,
                                display: isEmpty(stateLocal.flowContaReceberVisualizarDTO?.contasApropriacoes.contas) ? "flex" : "table",
                            }}>
                                {isEmpty(stateLocal.flowContaReceberVisualizarDTO?.contasApropriacoes.contas) && (
                                    <Typography sx={{
                                        width: '100%',
                                        textAlign: "center",
                                        marginTop: "30px",
                                        fontSize: "30px",
                                        color: theme.palette.text.disabled,
                                        alignSelf: "center",
                                        userSelect: "none",
                                    }}>
                                        Nenhuma conta de apropriação encontrada.
                                    </Typography>
                                )}

                                {!isEmpty(stateLocal.flowContaReceberVisualizarDTO?.contasApropriacoes.contas) && (
                                    <TableHead sx={{
                                        width: '100%',
                                        borderBottom: `1px solid ${theme.palette.text.disabled}`,
                                        maxHeight: "55px",
                                        bgcolor: theme.palette.background.default
                                    }}>
                                        <TableRow sx={{
                                            border: 0,
                                            display: "flex"
                                        }}>
                                            <TableCell sx={{
                                                width: "85%",
                                                fontWeight: "bold",
                                                border: 0,
                                                padding: "5px",
                                                paddingLeft: "15px"
                                            }}>
                                                Conta
                                            </TableCell>
                                            <TableCell sx={{
                                                width: "15%",
                                                fontWeight: "bold",
                                                border: 0,
                                                padding: "5px",
                                                textAlign: "center"
                                            }}>
                                                Valor
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                )}

                                {!isEmpty(stateLocal.flowContaReceberVisualizarDTO?.contasApropriacoes.contas) && (
                                    stateLocal.flowContaReceberVisualizarDTO?.contasApropriacoes.contas.map((e, index, arr) => {
                                        return (
                                            <TableRow sx={{
                                                display: "flex",
                                                borderBottom: (index === arr.length - 1) ? 0 : `1px solid ${theme.palette.text.disabled}`,
                                            }}>
                                                <TableCell sx={{
                                                    width: "85%",
                                                    border: 0,
                                                    padding: "5px",
                                                    paddingLeft: "15px"
                                                }}>
                                                    {e.conta}
                                                </TableCell>

                                                <TableCell sx={{
                                                    width: "15%",
                                                    border: 0,
                                                    padding: "5px",
                                                    textAlign: "center",
                                                    alignSelf: "center"
                                                }}>
                                                    {currencyOf(e.valor)}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })
                                )}
                            </Table>
                        )}

                        {isObservacaoRender() && (
                            <Table sx={{
                                border: `2px solid ${theme.palette.primary.main}`,
                                display: isEmpty(stateLocal.flowContaReceberVisualizarDTO?.observacao ?? []) ? "flex" : "table",
                            }}>

                                {isEmpty(stateLocal.flowContaReceberVisualizarDTO?.observacao ?? []) && (
                                    <Typography sx={{
                                        width: '100%',
                                        textAlign: "center",
                                        fontSize: "30px",
                                        color: theme.palette.text.disabled,
                                        alignSelf: "center",
                                        userSelect: "none",
                                    }}>
                                        Nenhuma observação encontrada.
                                    </Typography>
                                )}

                                {!isEmpty(stateLocal.flowContaReceberVisualizarDTO?.observacao ?? []) && (
                                    <TableCell>
                                        {stateLocal.flowContaReceberVisualizarDTO?.observacao}
                                    </TableCell>
                                )}

                            </Table>
                        )}
                    </TableContainer>
                </Grid>
            </DialogContent>
        </CustomDialog >
    )
}

export default VisualizarContaReceber;
